const CHUNK_RELOAD_STORAGE_KEY = 'smart-portal:chunk-load-reload-at';
const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

const CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk [\w-]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
];

function errorText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return '';

  const candidate = value as { message?: unknown; name?: unknown };
  return [candidate.name, candidate.message]
    .filter((part): part is string => typeof part === 'string')
    .join(' ');
}

export function isChunkLoadFailure(value: unknown): boolean {
  const text = errorText(value);
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

export function isNextChunkScript(target: unknown): boolean {
  if (!target || typeof target !== 'object') return false;

  const candidate = target as { src?: unknown; tagName?: unknown };
  return (
    candidate.tagName === 'SCRIPT'
    && typeof candidate.src === 'string'
    && candidate.src.includes('/_next/static/chunks/')
  );
}

/**
 * A route change can fail before React renders its error UI when a Next.js
 * chunk download is interrupted. Reload the current URL once so the browser
 * can request a fresh document and retry the chunk, while the session cooldown
 * prevents a broken deployment from creating an infinite reload loop.
 */
export function setupChunkLoadRecovery(): () => void {
  if (typeof window === 'undefined') return () => undefined;

  let reloadStarted = false;

  const reloadOnce = () => {
    if (reloadStarted) return;

    const now = Date.now();
    let previousReload = 0;

    try {
      previousReload = Number(window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY)) || 0;
    } catch {
      // Storage can be unavailable in locked-down browser modes. The in-memory
      // guard still prevents duplicate reloads during this page lifetime.
    }

    if (now - previousReload < CHUNK_RELOAD_COOLDOWN_MS) return;

    reloadStarted = true;
    try {
      window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
    } catch {
      // Reload recovery remains useful even when sessionStorage is unavailable.
    }
    window.location.reload();
  };

  const handleError = (event: ErrorEvent) => {
    if (isNextChunkScript(event.target) || isChunkLoadFailure(event.error) || isChunkLoadFailure(event.message)) {
      reloadOnce();
    }
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (isChunkLoadFailure(event.reason)) reloadOnce();
  };

  window.addEventListener('error', handleError, true);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  return () => {
    window.removeEventListener('error', handleError, true);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  };
}
